import React, { useCallback, useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  GoogleAuthProvider,
  browserLocalPersistence,
  setPersistence,
  createUserWithEmailAndPassword,
  updateProfile,
  signInWithPopup,
} from 'firebase/auth';
import { auth } from '../firebase';
import { apiClient } from '../services/api';
import './Login.css'; // Reuse the same CSS as Login

const goaPoliceLogo = 'https://en.wikipedia.org/wiki/Special:FilePath/Emblem_of_Goa_Police.png';

const initialState = {
  officeName: '',
  email: '',
  password: '',
  confirmPassword: '',
};

export default function Signup() {
  const navigate = useNavigate();
  const [form, setForm] = useState(initialState);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [apiStatus, setApiStatus] = useState('checking'); // API health status

  // Silent API health check
  useEffect(() => {
    const checkAPI = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/scan-reports?limit=1`, {
          headers: {
            'X-API-Key': process.env.REACT_APP_API_KEY,
          },
        });
        setApiStatus(response.ok ? 'available' : 'unavailable');
      } catch (error) {
        console.warn('API check failed:', error.message);
        setApiStatus('unavailable');
      }
    };

    checkAPI();
    // No idle polling; keep one-time status check.
  }, []);

  const googleProvider = useMemo(() => {
    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({ prompt: 'select_account' });
    return provider;
  }, []);

  const setField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    if (errors[key]) {
      setErrors((e) => ({ ...e, [key]: undefined }));
    }
  };

  const validate = useCallback(() => {
    const next = {};
    if (!form.officeName.trim()) next.officeName = 'Office name is required';
    if (!form.email.trim()) next.email = 'Email required';
    if (!form.password) next.password = 'Password required';
    else if (form.password.length < 8) next.password = 'Min 8 characters';
    if (!form.confirmPassword) next.confirmPassword = 'Confirm your password';
    else if (form.password !== form.confirmPassword) next.confirmPassword = 'Passwords do not match';
    return next;
  }, [form]);

  const handleSignup = useCallback(async () => {
    const nextErrors = validate();
    if (Object.keys(nextErrors).length) {
      setErrors(nextErrors);
      return;
    }
    setLoading(true);
    try {
      // Step 1: Create Firebase Auth account
      await setPersistence(auth, browserLocalPersistence);
      const cred = await createUserWithEmailAndPassword(auth, form.email.trim(), form.password);
      
      // Step 2: Update Firebase profile
      await updateProfile(cred.user, { displayName: form.officeName.trim() });

      // Step 3: Store officer profile in backend API (if available)
      if (apiStatus === 'available') {
        try {
          await apiClient('/officer-profiles', 'POST', {
            uid: cred.user.uid,
            fullName: form.officeName.trim(),
            email: form.email.trim(),
            createdAt: new Date().toISOString(),
          });
        } catch (apiError) {
          console.warn('Officer profile API call failed:', apiError);
          // Don't fail signup if backend is down
        }
      }

      navigate('/home', { replace: true });
    } catch (err) {
      if (err?.code === 'auth/email-already-in-use') setErrors({ email: 'Email already registered' });
      else setErrors({ general: 'Signup failed. Please try again.' });
    } finally {
      setLoading(false);
    }
  }, [form, navigate, validate, apiStatus]);

  const handleGoogle = useCallback(async () => {
    setLoading(true);
    setErrors({});
    try {
      await setPersistence(auth, browserLocalPersistence);
      await signInWithPopup(auth, googleProvider);
      navigate('/home', { replace: true });
    } catch (err) {
      setErrors({ general: 'Google sign-in failed. Please try again.' });
    } finally {
      setLoading(false);
    }
  }, [googleProvider, navigate]);

  return (
    <div className="login-container">
      {/* Left Panel - Registration Form */}
      <div className="login-panel">
        <div className="login-content">
          <div className="login-header">
            <img src={goaPoliceLogo} alt="Goa Police" className="police-logo" />
            <h1>Officer Registration</h1>
          </div>

          <div className="login-form">
            <div className="form-group">
              <label htmlFor="officeName">Office Name *</label>
              <input
                id="officeName"
                type="text"
                value={form.officeName}
                onChange={(e) => setField('officeName', e.target.value)}
                placeholder="Enter office name"
                className="form-input"
                disabled={loading}
              />
              {errors.officeName && <div className="error-message">{errors.officeName}</div>}
            </div>

            <div className="form-group">
              <label htmlFor="email">Gmail *</label>
              <input
                id="email"
                type="text"
                value={form.email}
                onChange={(e) => setField('email', e.target.value)}
                placeholder="Enter your email"
                className="form-input"
                disabled={loading}
              />
              {errors.email && <div className="error-message">{errors.email}</div>}
            </div>

            <div className="form-group">
              <label htmlFor="password">Secure Password *</label>
              <input
                id="password"
                type="password"
                value={form.password}
                onChange={(e) => setField('password', e.target.value)}
                placeholder="Minimum 8 characters"
                className="form-input"
                disabled={loading}
              />
              {errors.password && <div className="error-message">{errors.password}</div>}
            </div>

            <div className="form-group">
              <label htmlFor="confirmPassword">Confirm Password *</label>
              <input
                id="confirmPassword"
                type="password"
                value={form.confirmPassword}
                onChange={(e) => setField('confirmPassword', e.target.value)}
                placeholder="Re-enter your password"
                className="form-input"
                disabled={loading}
              />
              {errors.confirmPassword && <div className="error-message">{errors.confirmPassword}</div>}
            </div>

            {errors.general && <div className="error-message">{errors.general}</div>}

            <button
              type="button"
              className="secure-login-btn"
              onClick={handleSignup}
              disabled={loading}
            >
              {loading ? 'Creating account...' : 'Register Securely'}
            </button>

            <div className="divider">
              <span>OR</span>
            </div>

            <button
              type="button"
              className="google-login-btn"
              onClick={handleGoogle}
              disabled={loading}
            >
              <svg width="18" height="18" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Continue with Google
            </button>
          </div>

          <div className="login-footer">
            <p>Already have an account? <a href="/login" className="register-link">Login here</a></p>
          </div>
        </div>
      </div>

      {/* Right Panel - Branding */}
      <div className="branding-panel">
        <div className="branding-content">
          <h2>Cyber Vigil</h2>
          <p>Secure onboarding for cyber threat monitoring across states and platforms.</p>
        </div>
      </div>
    </div>
  );
}

